import { useEffect, useRef } from 'react'
import { ReceiptIcon } from '@/components/icons/ReceiptIcon'
import { RequestDateIcon } from '@/components/icons/RequestDateIcon'
import { AWS_S3_BUCKET, HARDWARE_ITEMS_TYPES, INVOICE_TYPES } from '@/constants'
import {
  IInvoiceDetailsFacility,
  IInvoiceDetailsMasterNode,
  IInvoiceDetailsMiner,
  IRepairBreakdown,
} from '@/types/billing.interface'
import { formatPrice } from '@/utils/formatPrice'
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionIcon,
  AccordionPanel,
  Box,
  HStack,
  VStack,
  Text,
  Image,
  useColorModeValue,
  Stack,
  useBreakpointValue,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  Badge,
  Button,
  PopoverArrow,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import { uniqBy } from 'lodash'

import getHashrateValue from '@/utils/getHashrateValue'
import { FlagCheckbox } from '@/components/FlagCheckbox'
import { ShippingIcon } from '@/components/icons/ShippingIcon'
import { FanIcon } from '@/components/icons/FanIcon'
import { HashboardIcon } from '@/components/icons/HashboardIcon'
import { ControlBoardIcon } from '@/components/icons/ControlBoardIcon'
import { SocketIcon } from '@/components/icons/SocketIcon'
import { DiscountIcon } from '@/components/icons/DiscountIcon'

interface BillingAccordionProps {
  details: IInvoiceDetailsMasterNode[] | IInvoiceDetailsMasterNode
  inline?: boolean
  isInvoicePaymentPage?: boolean
  selectedInvoices?: string[]
  onInvoiceSelectionChange?: (invoiceId: string, isSelected: boolean) => void
  showAnnouncement?: boolean
  onFeatureAnnouncementClose?: () => void
  showCheckboxes?: boolean
}

export const BillingAccordion = ({
  details,
  inline = false,
  isInvoicePaymentPage = false,
  selectedInvoices,
  onInvoiceSelectionChange,
  showAnnouncement = false,
  onFeatureAnnouncementClose,
  showCheckboxes = false,
}: BillingAccordionProps) => {
  const t = useTranslations()
  const grayWhite = useColorModeValue('#333', 'white')
  const isMobile = useBreakpointValue({ base: false, md: true }) ?? false

  const facilities = Array.isArray(details)
    ? details.flatMap((inv) => inv.facilitiesWithMiners || [])
    : details?.facilitiesWithMiners || []
  const hardwareMiners = Array.isArray(details)
    ? details.flatMap((inv) => inv.hardwareMiners || [])
    : details?.hardwareMiners || []

  const detailsArray = Array.isArray(details) ? details : [details]

  const facilitiesCountryFlag = (countryName: string) => {
    if (countryName) {
      if (countryName === 'United States') {
        return '/icons/flags/us.svg'
      }
      if (countryName === 'Canada') {
        return '/icons/flags/canada.svg'
      }
    }
    return '/icons/flags/globe.svg'
  }

  // get all invoice IDs for a facility based on the current details
  const getFacilityInvoiceIds = (
    facility: IInvoiceDetailsFacility
  ): string[] => {
    const parentDetail = detailsArray.find((detail) =>
      detail.facilitiesWithMiners?.some((f) => f === facility)
    )
    return parentDetail ? [parentDetail.invoiceId] : []
  }

  // check if all invoices in a facility are selected
  const isFacilityFullySelected = (
    facility: IInvoiceDetailsFacility
  ): boolean => {
    const facilityInvoiceIds = getFacilityInvoiceIds(facility)
    return (
      facilityInvoiceIds.length > 0 &&
      facilityInvoiceIds.every(
        (id) => selectedInvoices && selectedInvoices.includes(id)
      )
    )
  }

  const handleFacilitySelectionChange = (
    facility: IInvoiceDetailsFacility,
    isSelected: boolean
  ) => {
    if (!onInvoiceSelectionChange) return

    const facilityInvoiceIds = getFacilityInvoiceIds(facility)
    facilityInvoiceIds.forEach((invoiceId) => {
      onInvoiceSelectionChange(invoiceId, isSelected)
    })
  }

  if ((!facilities || facilities.length === 0) && hardwareMiners) {
    return (
      <MinersList
        miners={hardwareMiners}
        details={details}
        t={t}
        isSelected={false}
      />
    )
  }

  const qtyLabel = (qty: number, display: boolean) => {
    return (
      <Text
        fontSize={{
          base: '14px',
          md: '16px',
          lg: '15px',
          xl: '18px',
        }}
        color={grayWhite}
        display={display ? 'inherit' : 'none'}
      >{`${t('dashboard.billing.qty')}: ${qty}`}</Text>
    )
  }

  return (
    <Accordion allowMultiple>
      {facilities &&
        facilities?.map((facility: IInvoiceDetailsFacility, index: number) => {
          const isSelected = isFacilityFullySelected(facility)
          const lineItemOpacity = isInvoicePaymentPage && !isSelected ? 0.5 : 1
          const shouldShowAnnouncement =
            index === 0 && showAnnouncement && isInvoicePaymentPage

          return (
            <AccordionItem
              key={`facility-${facility?.facilityCountry}-${index}`}
              border="none"
            >
              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                <Stack
                  direction={inline && isMobile ? 'row' : 'column'}
                  align={inline && isMobile ? 'center' : 'stretch'}
                  width="100%"
                  fontSize={{ base: '14px', md: '16px', lg: '18px' }}
                  fontWeight="400"
                  justify="space-between"
                >
                  <HStack spacing={2} align="center">
                    {onInvoiceSelectionChange && showCheckboxes ? (
                      shouldShowAnnouncement ? (
                        <FeatureAnnouncement
                          isOpen={showAnnouncement}
                          onClose={onFeatureAnnouncementClose || (() => {})}
                          description={t(
                            'dashboard.billing.partial-payment-description'
                          )}
                        >
                          <Box onClick={(e) => e.stopPropagation()}>
                            <FlagCheckbox
                              flagSrc={facilitiesCountryFlag(
                                facility.facilityCountry
                              )}
                              flagAlt={facility.facilityCountry}
                              isChecked={isSelected}
                              onChange={(checked) => {
                                handleFacilitySelectionChange(facility, checked)
                              }}
                            />
                          </Box>
                        </FeatureAnnouncement>
                      ) : (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <FlagCheckbox
                            flagSrc={facilitiesCountryFlag(
                              facility.facilityCountry
                            )}
                            flagAlt={facility.facilityCountry}
                            isChecked={isSelected}
                            onChange={(checked) => {
                              handleFacilitySelectionChange(facility, checked)
                            }}
                          />
                        </Box>
                      )
                    ) : (
                      <Image
                        src={facilitiesCountryFlag(facility.facilityCountry)}
                        alt={facility.facilityCountry}
                        w="25px"
                        h="18px"
                      />
                    )}
                    <Text
                      fontSize={{
                        base: '14px',
                        md: '16px',
                        lg: '15px',
                        xl: '18px',
                      }}
                      fontWeight="500"
                      color={grayWhite}
                      mr={5}
                      opacity={lineItemOpacity}
                    >
                      {facility.facilityName}
                    </Text>
                    <Box as={'span'} opacity={lineItemOpacity}>
                      {qtyLabel(facility.miners.length, inline && isMobile)}
                    </Box>
                  </HStack>
                  <HStack spacing={4} mr="24px" opacity={lineItemOpacity}>
                    {qtyLabel(facility.miners.length, !(inline && isMobile))}
                    <Text
                      fontSize={{
                        base: '14px',
                        md: '16px',
                        lg: '15px',
                        xl: '18px',
                      }}
                      color={grayWhite}
                    >{`${t('dashboard.miners.total-fees')}: ${formatPrice(
                      facility.facilityTotalFees
                    )}`}</Text>
                  </HStack>
                </Stack>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel px={0} py={2} opacity={lineItemOpacity}>
                <VStack spacing={4} align="stretch">
                  <MinersList
                    miners={facility.miners}
                    details={details}
                    t={t}
                    isSelected={isSelected}
                  />
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          )
        })}
    </Accordion>
  )
}

const MinersList = ({
  miners,
  details,
  t,
  isSelected = true,
  isInvoicePaymentPage = false,
}: {
  miners: IInvoiceDetailsMiner[]
  details: IInvoiceDetailsMasterNode[] | IInvoiceDetailsMasterNode
  t: any
  isSelected: boolean
  isInvoicePaymentPage?: boolean
}) => {
  const alpha700 = useColorModeValue('blackAlpha.700', 'whiteAlpha.700')
  const textColor = useColorModeValue('black', 'white')
  const minerOpacity = isInvoicePaymentPage && !isSelected ? 0.5 : 1

  const isHostingOrProtection = Array.isArray(details)
    ? details.some(
        (inv) =>
          inv.invoiceType === INVOICE_TYPES.HOSTING ||
          inv.invoiceType === INVOICE_TYPES.PROTECTION
      )
    : details?.invoiceType === INVOICE_TYPES.HOSTING ||
      details?.invoiceType === INVOICE_TYPES.PROTECTION

  const isHosting = Array.isArray(details)
    ? details.some((inv) => inv.invoiceType === INVOICE_TYPES.HOSTING)
    : details?.invoiceType === INVOICE_TYPES.HOSTING

  const hashRateDisplay = (algorithm: string, hashrate: number) => {
    const hashRate = getHashrateValue(algorithm, hashrate, false)
    return hashRate.replace('/s', '')
  }

  const isProtection = Array.isArray(details)
    ? details.some((inv) => inv.invoiceType === INVOICE_TYPES.PROTECTION)
    : details?.invoiceType === INVOICE_TYPES.PROTECTION

  const isRepair = Array.isArray(details)
    ? details.some(
        (inv) =>
          inv.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.REPAIR &&
          inv.repairBreakdown !== null
      )
    : details?.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.REPAIR

  const repairBreakdowns: IRepairBreakdown[] = Array.isArray(details)
    ? details.flatMap((inv) => inv.repairBreakdown || [])
    : details?.repairBreakdown || []

  const minersFiltered = isRepair ? uniqBy(miners, 'minerId') : miners

  const isFinancing = Array.isArray(details)
    ? details.some(
        (inv) => inv.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.FINANCING
      )
    : details?.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.FINANCING

  const isCredits = Array.isArray(details)
    ? details.some(
        (inv) => inv.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.CREDITS
      )
    : details?.hardwareInvoiceType === HARDWARE_ITEMS_TYPES.CREDITS

  const label = isFinancing
    ? t('dashboard.billing.payment-title')
    : isProtection
    ? t('common.labels.cost')
    : t('dashboard.billing.fee')

  const getImageUrl = (path: string): string => {
    if (!path) return '/img/MS20_BACK.png'
    return path.startsWith('http') ? path : AWS_S3_BUCKET + path
  }

  const getMinerImage = (imageUrl: string) => {
    if (imageUrl) {
      return getImageUrl(imageUrl)
    }
    return '/img/MS20_BACK.png'
  }

  const getRepairTypeIcon = (name: string) => {
    if (name.includes('fan')) return FanIcon
    if (name.includes('hashboard')) return HashboardIcon
    if (name.includes('control board')) return ControlBoardIcon
    if (name.includes('psu')) return SocketIcon
    return ControlBoardIcon
  }

  return (
    <Box width="100%" opacity={minerOpacity}>
      <Flex direction="column" align="flex-start" width="100%">
        {minersFiltered?.map((miner: IInvoiceDetailsMiner) => {
          const repairBreakdown = repairBreakdowns.find(
            (rb) => rb.shortId === miner.minerShortId
          )
          return (
            <HStack
              key={miner?.minerShortId}
              p={3}
              spacing={3}
              align="start"
              w="100%"
              gap="16px"
            >
              <Box
                w="87px"
                h="75px"
                position={'relative'}
                bg="gray.100"
                rounded={'md'}
                display={{ base: 'none', lg: 'flex' }}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Image
                  position="absolute"
                  objectPosition="center"
                  objectFit={'cover'}
                  w="90%"
                  h="90%"
                  src={
                    getMinerImage(miner?.minerImageUrl) || '/img/MS20_BACK.png'
                  }
                  alt="Miner Image"
                />
              </Box>
              <VStack align="start" justify="center" spacing={1} w="100%">
                <Text fontWeight="600">{`${miner?.minerShortId} | ${
                  miner?.minerModelName
                } ${hashRateDisplay(
                  miner?.minerAlgorithm,
                  miner?.minerHashrate
                )}`}</Text>
                <Flex color={alpha700} direction="column" gap={1}>
                  {isHostingOrProtection &&
                    miner?.minerStartDate &&
                    miner?.minerEndDate && (
                      <HStack>
                        <RequestDateIcon />
                        <Text fontSize="14px" fontWeight="400">
                          {`${
                            isHosting
                              ? t('dashboard.billing.date')
                              : t('dashboard.billing.term')
                          }: ${format(
                            new Date(miner?.minerStartDate),
                            'MMM do yyyy'
                          )} - ${format(
                            new Date(miner?.minerEndDate),
                            'MMM do yyyy'
                          )}`}
                        </Text>
                      </HStack>
                    )}
                  {isFinancing && (
                    <HStack>
                      <RequestDateIcon />
                      <Text fontSize="14px" fontWeight="400">
                        {`${t('dashboard.billing.part')}: ${
                          Array.isArray(details)
                            ? details[0]?.financingInstallmentNumber
                            : details?.financingInstallmentNumber
                        }/${
                          Array.isArray(details)
                            ? details[0]?.financingTotalInstallments
                            : details?.financingTotalInstallments
                        }`}
                      </Text>
                    </HStack>
                  )}

                  {isProtection && (
                    <HStack>
                      <RequestDateIcon />
                      <Text fontSize="14px" fontWeight="400">
                        {`${t(
                          'dashboard.miners.protection-plans.table.coverage-amount'
                        )}: ${formatPrice(miner?.minerCoverageAmount)}`}
                      </Text>
                    </HStack>
                  )}
                </Flex>
                {!isCredits && !isRepair && (
                  <HStack color={alpha700}>
                    <ReceiptIcon />
                    <Text fontSize="14px" fontWeight="400">
                      {`${label}: ${formatPrice(miner?.minerInvoiceFee)}`}
                    </Text>
                  </HStack>
                )}
                {Number(miner.minerTaxFee) > 0 && (
                  <HStack color={alpha700}>
                    <ReceiptIcon />
                    <Text fontSize="14px" fontWeight="400">
                      {`${t('dashboard.billing.tax')}: ${formatPrice(
                        miner?.minerTaxFee
                      )}`}
                    </Text>
                  </HStack>
                )}
                {Number(miner.minerLateFee) > 0 && (
                  <HStack color={alpha700}>
                    <ReceiptIcon />
                    <Text fontSize="14px" fontWeight="400">
                      {`${t('dashboard.billing.late-fee')}: ${formatPrice(
                        miner?.minerLateFee
                      )}`}
                    </Text>
                  </HStack>
                )}
                {isRepair && (
                  <VStack color={alpha700} gap="8px" fontSize="14px" w="100%">
                    {repairBreakdown?.repairTypes?.map((repairType) => {
                      const name = repairType.repairTypeName.toLowerCase()
                      const Icon = getRepairTypeIcon(name)

                      return (
                        <HStack
                          key={repairType.repairTypeId}
                          gap="12px"
                          w="100%"
                        >
                          <Icon boxSize="18px" />
                          <HStack justify="space-between" w="full">
                            <Text>{repairType.repairTypeName}</Text>
                            <Text color={textColor}>
                              {formatPrice(repairType.repairTypeCost)}
                            </Text>
                          </HStack>
                        </HStack>
                      )
                    })}
                    {repairBreakdown?.recordedCustomerShippingFee &&
                      Number(repairBreakdown.recordedCustomerShippingFee) !==
                        0 && (
                        <HStack gap="12px" w="100%">
                          <ShippingIcon boxSize="18px" />
                          <HStack justify="space-between" w="full">
                            <Text>
                              {t('dashboard.services.shipping-costs')}
                            </Text>
                            <Text color={textColor}>
                              {formatPrice(
                                repairBreakdown.recordedCustomerShippingFee
                              )}
                            </Text>
                          </HStack>
                        </HStack>
                      )}
                    {repairBreakdown?.discount &&
                      Number(repairBreakdown.discount) !== 0 && (
                        <HStack gap="12px" w="100%">
                          <DiscountIcon boxSize="18px" />
                          <HStack justify="space-between" w="full">
                            <Text>{t('common.labels.discount')}</Text>
                            <Text color="brandGreen.50">
                              {`-${formatPrice(repairBreakdown.discount)}`}
                            </Text>
                          </HStack>
                        </HStack>
                      )}
                    <HStack
                      justify="space-between"
                      w="full"
                      fontWeight="600"
                      color={textColor}
                    >
                      <Text>{t('dashboard.services.repair-cost')}</Text>
                      <Text>
                        {formatPrice(
                          repairBreakdown?.invoicedTotalCustomerFee ?? 0
                        )}
                      </Text>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            </HStack>
          )
        })}
      </Flex>
    </Box>
  )
}

interface FeatureAnnouncementProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  description?: string
  ctaText?: string
}

const FeatureAnnouncement = ({
  isOpen,
  onClose,
  children,
  description,
  ctaText = 'Got it',
}: FeatureAnnouncementProps) => {
  const t = useTranslations()
  const bgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('black', 'white')
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        popoverRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
        return

      e.stopPropagation()
      e.preventDefault()
    }

    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [isOpen])

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      closeOnBlur={false}
      closeOnEsc={true}
      arrowSize={12}
      offset={[0, 10]}
    >
      <PopoverTrigger>
        <Box ref={triggerRef} position="relative" display="inline-block">
          {children}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverRef}
        bg={bgColor}
        borderWidth="2px"
        maxW="344px"
        maxH="160px"
        boxShadow="xl"
        p={2}
        _focus={{ boxShadow: 'xl' }}
        cursor="default"
        zIndex="popover"
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverArrow bg={bgColor} />
        <PopoverCloseButton />

        <PopoverHeader fontWeight="semibold" border="none" pb={2}>
          <Badge
            colorScheme="blue"
            bg="blue.100"
            color="blue.800"
            variant="solid"
            fontSize="xs"
            borderRadius="md"
            px={2}
            py={1}
            float={'left'}
          >
            {t('common.labels.new')}
          </Badge>
        </PopoverHeader>
        <PopoverBody pb={4}>
          <Text fontSize="sm" mb={4} color={textColor} fontWeight={'500'}>
            {description}
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={onClose}
            width="61px"
            h="32px"
            float="right"
          >
            {ctaText}
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
